import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { postNamedQuery } from '../../../../service'
import { useParameterReplacementEx } from '../../../../hooks/useAbundanceEngineParameters'
import { useVariables } from '../../../../providers/VariableProvider'

const PAGE_SIZE = 1000
export const MAX_ROWS = 10000
const EMPTY_ARRAY = []

// Pages until a short page or MAX_ROWS, for presentations that need the full dataset
// client-side (e.g. hierarchical tables).
export default function useExhaustiveBlueprintData (blueprintCode, purposeCode, options = {}) {
  const { parameters = {}, scopePredicates, definition, definitionVersionId } = options

  const { variables } = useVariables()
  const [resolvedParams] = useParameterReplacementEx(parameters, variables)
  const useInlineDefinition = !!definition
  const namedQuery = useInlineDefinition ? 'debug-query-blueprint' : 'execute-query-blueprint'

  const baseRequest = useMemo(() => {
    if (!purposeCode) return null
    if (!useInlineDefinition && !blueprintCode) return null
    const req = {
      purposeCode,
      parameters: resolvedParams
    }
    if (useInlineDefinition) {
      req.definition = definition
    } else {
      req.code = blueprintCode
      if (definitionVersionId) req.definitionVersionId = definitionVersionId
    }
    if (scopePredicates?.length) req.scopePredicates = scopePredicates
    return req
  }, [blueprintCode, purposeCode, resolvedParams, useInlineDefinition, definition, definitionVersionId, scopePredicates])

  const requestKey = JSON.stringify(baseRequest)

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['exhaustive-blueprint-data', namedQuery, blueprintCode, purposeCode, requestKey],
    queryFn: async () => {
      let allRows = []
      let page = 0
      let lastMetadata = null

      while (allRows.length < MAX_ROWS) {
        const { data: result } = await postNamedQuery('coreData', namedQuery, {
          ...baseRequest,
          page,
          pageSize: PAGE_SIZE
        })
        const pageData = result?.data ?? []
        lastMetadata = result?.metadata ?? lastMetadata
        allRows = allRows.concat(pageData)

        if (pageData.length < PAGE_SIZE) break
        page++
      }

      const truncated = allRows.length >= MAX_ROWS
      if (truncated) allRows = allRows.slice(0, MAX_ROWS)

      return { rows: allRows, metadata: lastMetadata, truncated }
    },
    enabled: !!baseRequest,
    keepPreviousData: true
  })

  return {
    data: data?.rows ?? EMPTY_ARRAY,
    metadata: data?.metadata ?? null,
    isLoading,
    isFetching,
    error,
    truncated: data?.truncated ?? false
  }
}
